import { Injectable } from '@angular/core';
import { IRecurrentBuy } from '@interfaces/recurrent-buys';
import { ErrorAndLoadingService } from '@services/error-loading/error-loading.service';
import { BehaviorSubject, Observable } from 'rxjs';

import { FirebaseService } from '../firebase/firebase.service';

@Injectable({
  providedIn: 'root',
})
export class RecurrentBuysService {
  private recurrentBuysSubject = new BehaviorSubject<IRecurrentBuy[]>([]);
  currentRecurrentBuys = this.recurrentBuysSubject.asObservable();
  private recurrentBuysIdsSubject = new BehaviorSubject<string[]>([]);
  currentRecurrentBuysIds = this.recurrentBuysIdsSubject.asObservable();

  constructor(
    private firebaseService: FirebaseService,
    private errorService: ErrorAndLoadingService,
  ) { }

  getRecurrentBuys(uid?: string) {
    if (!uid) {
      this.recurrentBuysSubject.next([]);
      this.recurrentBuysIdsSubject.next([]);
      return;
    }
    try {
      const query = this.firebaseService.getCollection('recurrentBuys').where('userId', '==', uid).where('status', '==', 'active');
      const parseRecurrentBuysFn = (querySnapshot) => {
        const result = querySnapshot.docs.map((document) => document.data()) as IRecurrentBuy[];
        this.recurrentBuysIdsSubject.next(result.map(b => b.assetId));
        return result;
      };
      this.firebaseService.observeQuery(query, this.recurrentBuysSubject, parseRecurrentBuysFn);
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener las compras recurrentes', false);
      throw err;
    }
  }

  async getRecurrentBuyById(id: string) {
    try {
      const collection = this.firebaseService.getCollection('recurrentBuys');
      const recurrentBuyRef = await this.firebaseService.getDocumentObject(id, collection);
      if (!recurrentBuyRef.exists) {
        return null;
      }
      return recurrentBuyRef.data() as IRecurrentBuy;
    } catch (err) {
      throw err;
    }
  }

  async inactiveRecurrentBuys(id: string) {
    return await this.firebaseService.updateDocument({ status: 'inactive' }, this.firebaseService.getCollection('recurrentBuys'), id);
  }

  async updateRecurrentBuy(recurrentBuyId: string, updateFields: any) {
    const recurrentBuysRef = this.firebaseService.getCollection(`recurrentBuys`);
    return new Observable(observer => {
      const collection = this.firebaseService.getCollection('recurrentBuys');
      this.firebaseService.updateDocument(updateFields, recurrentBuysRef, recurrentBuyId);
      const unsubscribe = this.firebaseService.firestore.doc(`/recurrentBuys/${recurrentBuyId}`).onSnapshot(
        (doc) => {
          if (doc.data().status === 'active') {
            observer.next(true);
            unsubscribe();
          } else if (doc.data().status === 'inactive') {
            observer.next(false);
            unsubscribe();
          } else {
            return;
          }
        },
        () => {
          observer.next(false);
          unsubscribe();
        },
      );
    });
  }

  async createRecurrentBuy(recurrentBuy: IRecurrentBuy) {
    return new Observable(observer => {
      const collection = this.firebaseService.getCollection('recurrentBuys');
      this.firebaseService.addDocument(recurrentBuy, collection)
      .then(async docRef => {
        this.firebaseService.updateDocument({id: docRef.id}, collection, docRef.id);
        const unsubscribe = this.firebaseService.firestore.doc(`/recurrentBuys/${docRef.id}`).onSnapshot(
          (doc) => {
            if (doc.data().status === 'active') {
              observer.next(true);
              unsubscribe();
            } else if (doc.data().status === 'inactive') {
              observer.next(false);
              unsubscribe();
            } else {
              return;
            }
          },
          () => {
            observer.next(false);
            unsubscribe();
          },
        );
      })
      .catch(err => {
        this.firebaseService.logError(err.message);
        observer.next(false);
      });
    });
  }
}
